import React, { FC, memo, useEffect, useState } from 'react';
import NumberInput from '~ComponentsImproved/Numpad';
import Icon from '~ComponentsImproved/Icon';
import Heading from '~ComponentsImproved/Typography/Heading';
import BeeperIcon from '~Assets/icons/beeper.svg';
import EditIcon from '~Assets/icons/edit.svg';
import CloseIcon from '~Assets/icons/close.svg';
import { useSelector, useButton, useMetadata } from '~Hooks';
import BeeperSelect from '~Components/BeeperSelect';
import { useCart } from '~Components/CartProvider';
import { TransparentButton } from '~Styles';
import styled from '@emotion/styled';

const OrderNumberButton = styled(TransparentButton)`
  padding: 0;
`;

/* Component responsible for changing the order number - This includes changing it with Beepers aswell (if users have created atleast one in VM) */
const ChangeOrderNumber: FC = () => {
  const cart = useCart();
  const metadata = useMetadata();

  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [originalNo, setOriginalNo] = useState<null | string>(null);
  const [orderNo, setOrderNo] = useState<null | string>(cart.easyId);

  const [showNumberInput, setShowNumberInput] = useState<boolean>(false);
  const [showBeeperSelector, setShowBeeperSelector] = useState<boolean>(false);

  const hasBeepers = metadata.data.beeperList.length > 0;

  useEffect(() => {
    setOrderNo(cart.easyId);

    if (!originalNo && cart.easyId) setOriginalNo(cart.easyId);
    if (orderNo && cart.easyId !== orderNo) setHasChanged(false);
  }, [cart.easyId]);

  const setFlag = (number: string) => {
    setOrderNo(number);
    cart.setEasyId(number);
    setHasChanged(true);
    setShowNumberInput(false);
  };

  const orderNumber = orderNo ? (orderNo.includes('•') ? orderNo : '#' + orderNo) : '--';

  const changeOrderNumberButton = useButton({
    onClick: () => {
      if (hasChanged) {
        setHasChanged(false);
        cart.setEasyId(originalNo);
        return;
      }

      if (hasBeepers) {
        setShowBeeperSelector(true);
        return;
      }

      setShowNumberInput(true);
    },
  });

  return (
    <>
      <OrderNumberButton
        display="flex"
        gap="xs"
        alignItems="center"
        justifyContent="center"
        {...changeOrderNumberButton}>
        <Heading level={2} color={hasChanged ? 'var(--colorUISand)' : 'var(--colorWhite)'}>
          {orderNumber}
        </Heading>
        {hasChanged ? (
          <Icon src={CloseIcon} color="var(--colorBlueGray900)" />
        ) : (
          <Icon src={hasBeepers ? BeeperIcon : EditIcon} color="var(--colorBlueGray900)" />
        )}
      </OrderNumberButton>

      {showNumberInput && (
        <NumberInput
          onClose={() => setShowNumberInput(false)}
          onSubmit={(number) => setFlag(number)}
          maxValue={999}
        />
      )}

      {showBeeperSelector && (
        <BeeperSelect
          beeperList={metadata.data.beeperList}
          onSelect={(beeperId) => {
            setFlag(beeperId);
            setShowBeeperSelector(false);
          }}
          onCancel={() => setShowBeeperSelector(false)}
        />
      )}
    </>
  );
};

export default memo(ChangeOrderNumber);
